import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    makeStyles,
} from '@material-ui/core';
import { CustomTextField } from '../Textfield';
import { Spinner } from '../UI';
import {
    staffKycApproveReject,
    getKycDetails,
} from '../../store/actions/staffActionCreator';
import { enqueueSnackbar } from '../../store/actions/notificationActionCreator';

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        padding: '20px 24px 12px',
    },
    titleText: {
        fontSize: '18px',
        fontWeight: 700,
        color: '#14112d',
    },
    subtitleText: {
        fontSize: '14px',
        color: '#808080',
        marginTop: '4px',
    },
    content: {
        padding: '16px 24px',
    },
    actions: {
        padding: '16px 24px',
        borderTop: '1px solid rgba(38, 50, 56, 0.08)',
    },
    approveBtn: {
        backgroundColor: '#4caf50',
        color: '#fff',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: '#388e3c',
        },
    },
    rejectBtn: {
        backgroundColor: '#f44336',
        color: '#fff',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: '#d32f2f',
        },
    },
    cancelBtn: {
        color: '#808080',
        fontWeight: 600,
    },
    warningText: {
        fontSize: '14px',
        color: '#ff9800',
        marginBottom: '16px',
    },
}));

export interface KYCApprovalModalProps {
    open: boolean;
    onClose: () => void;
    staffId: number;
    documentId: number;
    approvalStatus: 'APPROVED' | 'REJECTED';
    onSuccess?: () => void;
}

const KYCApprovalModal: React.FC<KYCApprovalModalProps> = ({
    open,
    onClose,
    staffId,
    documentId,
    approvalStatus,
    onSuccess,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    const isRejection = approvalStatus === 'REJECTED';

    const showMessage = useCallback(
        (msg: string, type: 'success' | 'error' = 'success') => {
            dispatch(
                enqueueSnackbar({
                    message: msg,
                    options: {
                        key: new Date().getTime() + Math.random(),
                        variant: type,
                    },
                }),
            );
        },
        [dispatch],
    );

    const dispatchKycApproveReject = useCallback(
        (payload: any, onSuccessCb: Function, onErrorCb: Function) => {
            dispatch(staffKycApproveReject(payload, onSuccessCb, onErrorCb));
        },
        [dispatch],
    );

    const dispatchGetKycDetails = useCallback(
        (id: number, onSuccessCb: Function, onErrorCb: Function) => {
            dispatch(getKycDetails(id, onSuccessCb, onErrorCb));
        },
        [dispatch],
    );

    const handleClose = () => {
        setRejectReason('');
        onClose();
    };

    const handleSubmit = async () => {
        if (isRejection && !rejectReason.trim()) {
            showMessage('Please enter a rejection reason', 'error');
            return;
        }

        setLoading(true);

        const payload = {
            staffId,
            data: {
                documents: [
                    {
                        id: documentId,
                        approvalStatus,
                        rejectReason: isRejection ? rejectReason.trim() : '',
                    },
                ],
            },
        };

        dispatchKycApproveReject(
            payload,
            () => {
                showMessage(
                    isRejection
                        ? 'Document rejected successfully'
                        : 'Document approved successfully',
                    'success',
                );

                // Refresh KYC details
                dispatchGetKycDetails(
                    staffId,
                    () => {
                        setLoading(false);
                        handleClose();
                        onSuccess?.();
                    },
                    () => {
                        setLoading(false);
                        handleClose();
                        onSuccess?.();
                    },
                );
            },
            () => {
                setLoading(false);
                showMessage(
                    isRejection
                        ? 'Failed to reject document'
                        : 'Failed to approve document',
                    'error',
                );
            },
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            aria-labelledby="kyc-approval-dialog"
        >
            <Spinner loading={loading}>
                <DialogTitle className={classes.dialogTitle} disableTypography>
                    <Typography className={classes.titleText}>
                        {isRejection ? 'Reject Document' : 'Approve Document'}
                    </Typography>
                    <Typography className={classes.subtitleText}>
                        {isRejection
                            ? 'Please provide a reason for rejection'
                            : 'Are you sure you want to approve this document?'}
                    </Typography>
                </DialogTitle>

                <DialogContent className={classes.content}>
                    {isRejection ? (
                        <CustomTextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Rejection Reason"
                            placeholder="Enter the reason for rejection..."
                            value={rejectReason}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setRejectReason(e.target.value)
                            }
                            variant="outlined"
                        />
                    ) : (
                        <Typography className={classes.warningText}>
                            This action will mark the document as verified. This
                            cannot be undone easily.
                        </Typography>
                    )}
                </DialogContent>

                <DialogActions className={classes.actions}>
                    <Button
                        onClick={handleClose}
                        className={classes.cancelBtn}
                        disableElevation
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        className={isRejection ? classes.rejectBtn : classes.approveBtn}
                        disableElevation
                        disabled={isRejection && !rejectReason.trim()}
                    >
                        {isRejection ? 'Reject' : 'Approve'}
                    </Button>
                </DialogActions>
            </Spinner>
        </Dialog>
    );
};

export default KYCApprovalModal;
